import { encerrarSessao } from "./sessao";
import { useCallback, useEffect, useState } from "react";
import { TABS } from "./config";
import { useFaixaDeAbas } from "./faixa-abas";
import type { Row } from "./types";
import { api, setSessionUser } from "./api";
import { ordenarPorNome } from "./ordem";
import { ICONS, IconDashboard, IconSun, IconMoon, IconLogout, IconHome } from "./icons";
import AnimatedBackground from "./components/AnimatedBackground";
import SolicitarAtualizacaoView from "./components/SolicitarAtualizacaoView";
import { AvisoReuniao } from "./components/PedirReuniaoView";
import Login from "./components/Login";
import logoBranca from "./assets/logo-branca.png";
import { applyTheme, getInitialTheme, type Theme } from "./theme";
import Dashboard from "./components/Dashboard";
import CrudView from "./components/CrudView";
import CargosView from "./components/CargosView";
import TriagemView from "./components/TriagemView";
import DiscView from "./components/DiscView";
import PesquisaClimaView from "./components/PesquisaClimaView";
import EndomarketingView from "./components/EndomarketingView";
import AvaliacaoDesempenhoView from "./components/AvaliacaoDesempenhoView";
import {
  SYSTEMS,
  readSsoUser,
  clearSsoFromUrl,
  goToSystem,
  checkAcesso,
  isAdminSupremo,
} from "./integration";
import AccessGate from "./components/AccessGate";
import AvisoSistema from "./components/AvisoSistema";
import { PermissoesProvider } from "./permissoes";
import {
  ACESSO_COMPLETO,
  ativarModoLeitura,
  podeVerAba,
  primeiraAbaLiberada,
  type Visualizacao,
} from "./modo-leitura";

const SESSION_KEY = "sercamp-user";

/** Estado do portão de acesso (checagem no Portal). */
type AccessState = "checking" | "allowed" | "denied" | "error";

/** Login inicial: prioriza o SSO vindo do Portal/DP (URL) e cai na sessão salva. */
function getInitialUser(): string | null {
  const sso = readSsoUser();
  if (sso) {
    localStorage.setItem(SESSION_KEY, sso);
    return sso;
  }
  return localStorage.getItem(SESSION_KEY);
}

/** Aba inicial via deep-link do Portal (#tab=chave); cai em "dashboard" se inválida. */
const INITIAL_TAB: string = (() => {
  try {
    const m = window.location.hash.match(/[#&]tab=([^&]+)/);
    const k = m ? decodeURIComponent(m[1]) : "";
    return k && TABS.some((t) => t.key === k) ? k : "dashboard";
  } catch {
    return "dashboard";
  }
})();

export default function App() {
  const [theme, setTheme] = useState<Theme>(getInitialTheme);
  const [user, setUser] = useState<string | null>(getInitialUser);

  useEffect(() => {
    clearSsoFromUrl();
  }, []);
  const [active, setActive] = useState<string>(INITIAL_TAB);
  const [colaboradores, setColaboradores] = useState<Row[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  // Excluir é exclusivo de administrador (regra do Portal): sem isto, os botões
  // de lixeira ficam escondidos para o resto do time.
  const [podeExcluir, setPodeExcluir] = useState(false);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [access, setAccess] = useState<AccessState>("checking");
  /* No celular o menu vira uma faixa que rola de lado: isto mantem a aba
     aberta a vista (ver src/faixa-abas.tsx). */
  const navRef = useFaixaDeAbas(active, access === "allowed");
  // Somente visualização (cadastro do Portal): abas liberadas e nada de gravar.
  const [vis, setVis] = useState<Visualizacao>(ACESSO_COMPLETO);

  // PORTÃO DE ACESSO: sempre que houver usuário atual (SSO novo ou sessão
  // restaurada), pergunta ao Portal se ele pode usar ESTE sistema antes de
  // liberar o app. O Admin Supremo é sempre liberado (e sempre admin).
  useEffect(() => {
    if (!user) {
      setAccess("checking");
      return;
    }
    let cancel = false;
    setAccess("checking");
    checkAcesso(user)
      .then((r) => {
        if (cancel) return;
        // Admin Supremo nunca é somente visualização.
        setVis(
          isAdminSupremo(user) ? ACESSO_COMPLETO : { somenteLeitura: r.somenteLeitura, abas: r.abas },
        );
        const ok = r.allowed || r.adminSupremo || isAdminSupremo(user);
        if (!ok) {
          // Acesso negado: anula a sessão salva (não deixa restaurar o app).
          localStorage.removeItem(SESSION_KEY);
          encerrarSessao();
          setSessionUser("");
        }
        setAccess(ok ? "allowed" : "denied");
      })
      .catch(() => {
        if (!cancel) setAccess("error");
      });
    return () => {
      cancel = true;
    };
  }, [user]);

  // Resolve se o usuário logado é administrador (define quem vê/gerencia "Usuários")
  // e busca o nome real cadastrado para exibir no topbar.
  useEffect(() => {
    setSessionUser(user ?? "");
    if (!user) {
      setIsAdmin(false);
      setPodeExcluir(false);
      setDisplayName(null);
      return;
    }
    api
      .me(user)
      .then((r) => {
        setIsAdmin(!!r.isAdmin);
        setPodeExcluir(!!r.podeExcluir);
        setDisplayName(r.nome || null);
      })
      .catch(() => {
        setIsAdmin(false);
        setPodeExcluir(false);
        setDisplayName(null);
      });
  }, [user]);

  // Não-admin não acessa a aba de usuários (nem por deep-link).
  useEffect(() => {
    if (active === "usuarios" && !isAdmin) setActive("dashboard");
  }, [active, isAdmin]);

  // Somente visualização: faixa de aviso, sem botões de gravar e sem gravação.
  useEffect(() => {
    ativarModoLeitura(access === "allowed" && vis.somenteLeitura);
  }, [access, vis.somenteLeitura]);

  // Aba fora das liberadas (inclusive por deep-link) cai na primeira liberada.
  const verAba = (k: string) =>
    podeVerAba(vis, k) && !(vis.somenteLeitura && (k === "usuarios" || k === "solicitar_atualizacao"));
  useEffect(() => {
    if (!verAba(active)) {
      const primeira = primeiraAbaLiberada(vis, ["dashboard", ...TABS.map((t) => t.key)].filter(verAba));
      if (primeira) setActive(primeira);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, vis]);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const toggleTheme = useCallback(
    () => setTheme((t) => (t === "dark" ? "light" : "dark")),
    [],
  );

  const loadColaboradores = useCallback(async () => {
    try {
      // A lista compartilhada por todas as telas (selects de colaborador,
      // "quem entra no link"...) já sai em ordem alfabética — src/ordem.ts.
      setColaboradores(ordenarPorNome(await api.list("colaboradores"), (c) => c.nome));
    } catch {
      /* mostrado nas telas individuais */
    }
  }, []);

  useEffect(() => {
    if (user && access === "allowed") loadColaboradores();
  }, [user, access, loadColaboradores]);

  const onDataChanged = useCallback(() => {
    loadColaboradores();
    setRefreshKey((k) => k + 1);
  }, [loadColaboradores]);

  function login(name: string) {
    localStorage.setItem(SESSION_KEY, name);
    setUser(name);
  }

  function logout() {
    localStorage.removeItem(SESSION_KEY);
    encerrarSessao();
    setUser(null);
    setVis(ACESSO_COMPLETO);
  }

  if (!user) {
    return <Login onLogin={login} theme={theme} onToggleTheme={toggleTheme} />;
  }

  // Portão de acesso: só renderiza o app quando o Portal liberar o usuário.
  if (access !== "allowed") {
    return (
      <AccessGate
        state={access}
        onBackToPortal={() => goToSystem(SYSTEMS.portal, user)}
      />
    );
  }

  const tab = TABS.find((t) => t.key === active);
  const title = active === "dashboard" ? "Dashboard" : tab?.label ?? "";
  const subtitle =
    active === "dashboard" ? "Visão geral dos indicadores de RH" : tab?.subtitle ?? "";

  return (
    <PermissoesProvider value={podeExcluir}>
      <AvisoSistema />
      <AvisoReuniao usuario={user} onAbrir={() => setActive("solicitar_atualizacao")} />
      <div className="app">
        <AnimatedBackground />

        <aside className="sidebar">
          <div className="brand">
            <img src={logoBranca} alt="SERCAMP" className="brand-logo-img" />
          </div>

          <nav className="nav" ref={navRef}>
            {verAba("dashboard") && (
              <button
                className={`nav-item ${active === "dashboard" ? "active" : ""}`}
                onClick={() => setActive("dashboard")}
              >
                <span className="nav-icon">
                  <IconDashboard size={19} />
                </span>
                Dashboard
              </button>
            )}
            {TABS.filter((t) => (t.key !== "usuarios" || isAdmin) && verAba(t.key)).map((t) => {
              const Icon = ICONS[t.icon];
              return (
                <button
                  key={t.key}
                  className={`nav-item ${active === t.key ? "active" : ""}`}
                  onClick={() => setActive(t.key)}
                >
                  <span className="nav-icon">
                    <Icon size={19} />
                  </span>
                  {t.label}
                </button>
              );
            })}
          </nav>

        </aside>

        <main className="content">
          <header className="topbar">
            <div>
              <h1>{title}</h1>
              <p className="muted">{subtitle}</p>
            </div>

            <div className="topbar-actions">
              <button
                className="theme-toggle"
                onClick={() => goToSystem(SYSTEMS.portal, user)}
                title="Portal SERCAMP"
              >
                <IconHome size={18} />
              </button>
              <button
                className="theme-toggle"
                onClick={toggleTheme}
                title={theme === "dark" ? "Tema claro" : "Tema escuro"}
              >
                {theme === "dark" ? <IconSun size={18} /> : <IconMoon size={18} />}
              </button>
              <div className="user-chip">
                <span className="user-avatar">
                  {user.charAt(0).toUpperCase()}
                </span>
                <span className="user-name">{displayName || user}</span>
              </div>
              <button className="theme-toggle" onClick={logout} title="Sair">
                <IconLogout size={18} />
              </button>
            </div>
          </header>

          <section className="view">
            {!verAba(active) ? null : active === "dashboard" ? (
              <Dashboard refreshKey={refreshKey} />
            ) : tab?.key === "desempenho" ? (
              // Avaliação de desempenho: plano de ação por colaborador.
              <AvaliacaoDesempenhoView key={tab.key} />
            ) : tab?.key === "endomarketing" ? (
              // Comunicação interna: WhatsApp, fotos, eventos e aniversariantes.
              <EndomarketingView key={tab.key} />
            ) : tab?.key === "engajamento" ? (
              // Pesquisa de Clima: o questionário oficial da SERCAMP aplicado
              // por link (um por colaborador) + o histórico de lançamentos.
              <PesquisaClimaView
                key={tab.key}
                tab={tab}
                colaboradores={colaboradores}
                onDataChanged={onDataChanged}
              />
            ) : tab?.key === "triagem" ? (
              // Triagem de currículo: vaga + perguntas (IA) + link do candidato.
              <TriagemView key={tab.key} />
            ) : tab?.key === "disc" ? (
              // DISC: aplicação do teste por link, correção pelo gabarito do RH
              // e o laudo com o cabeçalho da SERCAMP.
              <DiscView key={tab.key} />
            ) : tab?.key === "cargos" ? (
              // Cargos e Salários tem tela própria: o Plano de Cargos e
              // Salários 2026, com as seções da planilha (cargos, trilhas,
              // critérios, base vigente, movimentações e painel). Ver CargosView.tsx.
              <CargosView
                key={tab.key}
                tab={tab}
                colaboradores={colaboradores}
                onDataChanged={onDataChanged}
              />
            ) : tab?.key === "solicitar_atualizacao" ? (
              // A aba que existe em todos os sistemas: o pedido vai para o
              // sistema de Desenvolvimento (ver server/solicitar-atualizacao.ts).
              <SolicitarAtualizacaoView usuario={user} />
            ) : tab ? (
              <CrudView
                key={tab.key}
                tab={tab}
                colaboradores={colaboradores}
                onDataChanged={onDataChanged}
              />
            ) : null}
          </section>
        </main>
      </div>
    </PermissoesProvider>
  );
}
